/**
 * Hook: Wraps facility action handlers with try/catch and error toast.
 * Single Responsibility: centralize "execute action + show error on failure" (DRY).
 * Depends on: useFacilityActions (handlers + showErrorToast), ERROR_MESSAGES from constants.
 */

import { useCallback } from 'react';
import { ERROR_MESSAGES } from '../constants';

/**
 * Returns facility handlers wrapped with error feedback (toast on failure).
 * Stable references via useCallback so children don't re-render unnecessarily.
 */
export function useFacilityHandlersWithFeedback(actions) {
  const {
    handleAddFacility,
    handleUpdateFacility,
    handleDeleteFacility,
    handleDuplicateConfirm,
    showErrorToast,
  } = actions;

  const handleAddFacilityWithFeedback = useCallback(
    async (data) => {
      try {
        return await handleAddFacility(data);
      } catch (error) {
        showErrorToast(ERROR_MESSAGES.ADD_FACILITY)(error);
        throw error;
      }
    },
    [handleAddFacility, showErrorToast],
  );

  const handleUpdateFacilityWithFeedback = useCallback(
    async (id, data) => {
      try {
        return await handleUpdateFacility(id, data);
      } catch (error) {
        showErrorToast(ERROR_MESSAGES.UPDATE_FACILITY)(error);
        throw error;
      }
    },
    [handleUpdateFacility, showErrorToast],
  );

  const handleDeleteFacilityWithFeedback = useCallback(
    async (facilityId) => {
      try {
        return await handleDeleteFacility(facilityId);
      } catch (error) {
        showErrorToast(ERROR_MESSAGES.DELETE_FACILITY)(error);
        throw error;
      }
    },
    [handleDeleteFacility, showErrorToast],
  );

  return {
    handleAddFacilityWithFeedback,
    handleUpdateFacilityWithFeedback,
    handleDeleteFacilityWithFeedback,
    handleDuplicateConfirm,
    showErrorToast,
  };
}
